import { useContext, useEffect, useState } from "react";
import { Box } from "@mui/system";
import { filesData } from "../../../../scripts/context/FileStructure";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import { useOutletContext, useParams } from "react-router-dom";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import axios from "axios";
import toast from "react-hot-toast";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { AppContext } from "../../../../scripts/context/App";
import ConfirmationModal from "../../../ConfirmationModal";

interface IProps {
  fileDetails: filesData;
  allFiles: filesData[];
  handleClick: () => void;
  updateCompilationStatus: (fileDetails: filesData) => void;
}

export default function CustomNode({
  fileDetails,
  allFiles,
  handleClick,
  updateCompilationStatus,
}: IProps) {
  const { user } = useContext(AppContext);
  const {
    moveFile,
    handleDeleteTreeItem,
    project,
    currentPath,
    setState,
  }: any = useOutletContext();
  const { uuid } = useParams();
  const [edit, setEdit] = useState({
    status: false,
    name: fileDetails.text,
  });
  const [confirmDelete, setConfirmDelete] = useState({
    status: false,
    id: "",
    loading: false,
  });
  const [compiling, setCompiling] = useState(false);

  const STYLES = {
    root: {
      ...(allFiles[allFiles.length - 1].text !== fileDetails.text && {
        pb: 1,
        borderBottom: "1px solid #e0e0e0",
      }),
      display: "flex",
      alignItems: "cetner",
      justifyContent: "space-between",
      gap: 1,
    },
    icon_name: {
      ...(fileDetails.droppable &&
        !edit.status && {
        cursor: "pointer",
        "&:hover": { color: "#1976d2", textDecoration: "underline" },
      }),
      transition: "color 0.2s ease-in-out",
      display: "flex",
      alignItems: "center",
      gap: 1,
    },
    actions: {
      display: "flex",
      alignItems: "center",
      gap: 1,
    },
  };

  const NodeIcon = () =>
    fileDetails.droppable ? (
      <FolderOpenOutlinedIcon />
    ) : (
      <InsertDriveFileOutlinedIcon />
    );

  const updateName = async () => {
    if (!user.permissions.includes("edit_file_name"))
      return toast.error("You don't have permission to edit file names");

    if (!edit.name.split(".")[0].trim())
      return toast.error("Please enter a name");

    try {
      await axios.post("/deploy/edit-file-name", {
        project_uuid: uuid,
        old_path: `${currentPath}/${fileDetails.text}`,
        new_path: `${currentPath}/${edit.name}`,
      });

      toast.success(
        `${fileDetails.text} renamed to ${edit.name} successfully!`
      );
      setEdit({ ...edit, status: false });
      setState({
        project: {
          ...project,
          files_data: project.files_data.map((file: filesData) => {
            if (file.text === fileDetails.text) {
              return { ...file, text: edit.name };
            }

            return file;
          }),
        },
      });
    } catch (error: any) {
      toast.error(
        error.response.data.message || "Something went wrong. Please try again"
      );

      console.log({ error });
    }
  };

  const compileFile = async () => {
    if (!user.permissions.includes("compile"))
      return toast.error("You don't have permission to compile files");

    try {
      setCompiling(true);
      await axios.post("/deploy/compile", {
        project_uuid: uuid,
        file_name: `${currentPath}/${fileDetails.text}`,
      });

      toast.success(`${fileDetails.text} compiled successfully!`);
      updateCompilationStatus(fileDetails);
      setCompiling(false);
    } catch (error: any) {
      setCompiling(false);

      toast.error(
        error.response.data.message || "Something went wrong. Please try again"
      );
    }
  };

  useEffect(() => {
    setEdit({ ...edit, name: fileDetails.text });
  }, [fileDetails]);

  useEffect(() => {
    setEdit({ ...edit, status: false });
  }, [currentPath]);

  return (
    <>
      <ConfirmationModal
        open={confirmDelete.status}
        onClose={() => setConfirmDelete({ ...confirmDelete, status: false })}
        onConfirm={async () => {
          await handleDeleteTreeItem(
            fileDetails,
            `${currentPath}/${fileDetails.text}`
          );

          setConfirmDelete({ id: "", loading: false, status: false });
        }}
        title="Are you sure?"
        message={"This action cannot be undone, please confirm to continue"}
        loading={confirmDelete.loading}
      />
      <Box sx={STYLES.root}>
        <Box
          onClick={!edit.status ? handleClick : () => { }}
          sx={STYLES.icon_name}
        >
          <Tooltip title={`Move ${fileDetails.droppable ? "Folder" : "file"}`}>
            <IconButton
              color={
                [moveFile.old_path, moveFile.new_path].includes(
                  `${currentPath}/${fileDetails.text}`
                )
                  ? "primary"
                  : "default"
              }
              aria-label="move File"
              onClick={(event) => {
                if (!user.permissions.includes("move_file"))
                  return toast.error("You don't have permission to move files");

                const { old_path } = moveFile;
                event.stopPropagation();

                if (old_path && !fileDetails.droppable)
                  return toast.error(
                    "You can't move a folder or file to a file"
                  );
                if (old_path === `${currentPath}/${fileDetails.text}`)
                  return toast.error(
                    "You can't move a file to its current location"
                  );

                setState({
                  moveFile: {
                    ...(old_path
                      ? {
                        ...moveFile,
                        new_path: `${currentPath}/${fileDetails.text}`,
                      }
                      : { old_path: `${currentPath}/${fileDetails.text}` }),
                  },
                });
              }}
            >
              <DragIndicatorIcon />
            </IconButton>
          </Tooltip>
          {NodeIcon()}
          {edit.status ? (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                value={
                  fileDetails.droppable ? edit.name : edit.name.split(".")[0]
                }
                variant="standard"
                onChange={(event) => {
                  const { value: name } = event.target;

                  setEdit({
                    ...edit,
                    name: fileDetails.droppable ? name : `${name}.sol`,
                  });
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    updateName();
                  }
                }}
              />
              {!fileDetails.droppable && <span>.sol</span>}
            </Box>
          ) : (
            <Typography
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                width: "160px",
                whiteSpace: "nowrap",
              }}
              variant="body1"
            >
              {fileDetails.text}
            </Typography>
          )}
        </Box>
        <Box sx={STYLES.actions}>
          {!fileDetails.droppable && (
            <>
              <Chip label={fileDetails.data?.fileSize} variant="outlined" />
              <Tooltip title="Compile File">
                <IconButton
                  color="default"
                  aria-label="Compile File"
                  disabled={compiling}
                  onClick={() => {
                    if (!user.permissions.includes("compile"))
                      return toast.error(
                        "You don't have permission to compile files"
                      );

                    compileFile();
                  }}
                >
                  <GavelOutlinedIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip
            title={`${edit.status ? "Save The New" : "Edit"} ${fileDetails.droppable ? "Folder" : "File"
              } Name`}
          >
            <IconButton
              color="default"
              aria-label={`${edit.status ? "save the new" : "edit"} file name`}
              onClick={() => {
                if (!user.permissions.includes("edit_file_name"))
                  return toast.error(
                    "You don't have permission to edit file names"
                  );

                edit.status
                  ? updateName()
                  : setEdit({ ...edit, status: !edit.status });
              }}
            >
              {edit.status ? <SaveOutlinedIcon /> : <EditIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip
            title={`Delete ${fileDetails.droppable ? "Folder" : "File"}`}
          >
            <IconButton
              color="error"
              aria-label="delete file"
              onClick={() => {
                if (!user.permissions.includes("delete_file"))
                  return toast.error(
                    "You don't have permission to delete files"
                  );

                setConfirmDelete({ ...confirmDelete, status: true });
              }}
            >
              <DeleteOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
}
